import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Bell, Info, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import logger from '../../utils/logger';

const KIND_ICON = {
  info: Info, success: CheckCircle2, warning: AlertTriangle, error: XCircle,
};
const KIND_COLOUR = {
  info: 'text-[#111565]',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  error: 'text-red-600',
};

const NotificationBell = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications/inapp?limit=20');
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch (err) {
      logger.debug('notifications load failed', err);
    }
  }, [api]);

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  const handleClick = async (note) => {
    if (!note.read) {
      try {
        await api.post(`/notifications/inapp/${note.id}/read`);
        await load();
      } catch (err) {
        logger.debug('mark read failed', err);
      }
    }
    if (note.link) navigate(note.link);
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/inapp/read-all');
      await load();
    } catch (err) {
      logger.debug('mark all read failed', err);
    }
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full bg-[#ff6600] text-white text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 && (
            <button type="button" className="text-xs text-[#111565] hover:underline"
              onClick={markAllRead} data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">You're all caught up.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => <NotificationItem key={n.id} note={n} onClick={handleClick} />)}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NotificationItem = ({ note, onClick }) => {
  const Icon = KIND_ICON[note.kind] || Info;
  return (
    <button type="button"
      className={`w-full text-left flex gap-3 px-3 py-2 hover:bg-muted ${!note.read ? 'bg-amber-50/60' : ''}`}
      onClick={() => onClick(note)}
      data-testid={`notification-${note.id}`}>
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${KIND_COLOUR[note.kind] || ''}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{note.title}</p>
        <p className="text-xs text-muted-foreground line-clamp-2">{note.message}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
        </p>
      </div>
      {!note.read && <span className="h-2 w-2 rounded-full bg-[#ff6600] mt-2 flex-shrink-0" />}
    </button>
  );
};

export default NotificationBell;
